import { useEffect, useState } from 'react';
import L from 'leaflet';
import _ from 'lodash';
import { MapContainer, Marker, TileLayer, useMapEvents } from 'react-leaflet';
import { t, Trans } from '@lingui/macro';
import { makeStyles } from '@material-ui/core/styles';
import NgpTwoButtonDialog from '../../dialog-two-button/NgpTwoButtonDialog';
import { SiteAdminLocationField } from './SiteAdminLocationField';
import { DEFAULT_MAX_MAP_ZOOM, MIN_MAP_ZOOM } from '../../../services/SystemConfigService';
import NgpFormButton from '../../form/NgpFormButton';

const dialogTitle = t`Set Location`;
const defaultMapPosition = [10, 0];

const useStyles = makeStyles((theme) => ({
    locationContainer: {
        width: 840,
    },
    locationButtonContainer: {
        marginTop: theme.spacing(3),
    },
    map: {
        width: 840,
        height: 600,

        '& .leaflet-tile-pane': {
            filter: theme.palette.type === 'light' ? 'none' : 'invert(100%) hue-rotate(193deg) saturate(0.6)',
        },
        '&.leaflet-container': {
            width: '100%',
            background: theme.palette.type === 'light' ? 'rgb(152, 188, 218)' : 'rgb(59, 74, 97)',
        },
    },
}));

const tilesUrl = `/query/tile/1/{z}/{x}/{y}`;

const markerIcon = L.icon({
    iconUrl:
        'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24"><path d="M0 0h24v24H0z" fill="none"/><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="%233A6178"/></svg>',
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: null,
    shadowUrl: null,
    shadowSize: null,
    shadowAnchor: null,
});

function LocationMarker({ mapPosition, setMapPosition, setMapLocation, mapSearchPosition, setSearchLocation }) {
    const map = useMapEvents({
        click(e) {
            if (e.latlng) {
                setMapPosition([e.latlng.lat, e.latlng.lng]);
                setMapLocation({
                    latitude: _.toString(e.latlng.lat),
                    longitude: _.toString(e.latlng.lng),
                });
                map.flyTo(e.latlng, map.getZoom());
                setSearchLocation(null);
            }
        },
    });

    useEffect(() => {
        if (mapSearchPosition) {
            map.flyTo(mapSearchPosition, map.getZoom());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mapSearchPosition]);

    return !mapPosition ? null : <Marker icon={markerIcon} position={mapPosition} />;
}

function isPositionValid(value, min, max) {
    try {
        const valueAsNumber = parseFloat(value);
        if (_.isNaN(valueAsNumber)) {
            return false;
        }

        return valueAsNumber >= min && valueAsNumber <= max;
    } catch (e) {
        return false;
    }
}

function isValidLatLong(searchLocation) {
    if (!searchLocation || !searchLocation.latitude || !searchLocation.longitude) {
        return false;
    }

    if (_.isNaN(searchLocation.latitude) || _.isNaN(searchLocation.longitude)) {
        return false;
    }

    return isPositionValid(searchLocation.latitude, -90, 90) && isPositionValid(searchLocation.longitude, -180, 180);
}

export default function SiteAdminLocationDialog({
    defaultSearchText = '',
    onSetLocation,
    siteLatitude,
    siteLongitude,
}) {
    const classes = useStyles();
    const [showDialog, setShowDialog] = useState(false);
    const [searchLocation, setSearchLocation] = useState({});
    const [mapPosition, setMapPosition] = useState();
    const [mapLocation, setMapLocation] = useState();
    const [mapSearchPosition, setMapSearchPosition] = useState();

    const handleShowDialog = () => setShowDialog(true);
    const handleFormSubmit = () => {
        setShowDialog(false);
        cleanup();
        return onSetLocation(mapLocation);
    };

    useEffect(() => {
        if (isValidLatLong(searchLocation)) {
            setMapPosition([searchLocation.latitude, searchLocation.longitude]);
            setMapSearchPosition([searchLocation.latitude, searchLocation.longitude]);
            setMapLocation(searchLocation);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchLocation]);

    const cleanup = () => {
        setSearchLocation(null);
        setMapPosition(null);
        setMapLocation(null);
        setMapSearchPosition(null);
    };

    return (
        <>
            <div className={classes.locationButtonContainer}>
                <NgpFormButton onClick={handleShowDialog}>
                    <Trans>{dialogTitle}</Trans>
                </NgpFormButton>
            </div>
            <NgpTwoButtonDialog
                title={dialogTitle}
                confirmButtonText={<Trans>Set</Trans>}
                cancelButtonText={<Trans>Cancel</Trans>}
                open={showDialog}
                setOpen={setShowDialog}
                confirmButtonDisabled={!mapPosition}
                callerWillCloseOnConfirm
                onConfirm={handleFormSubmit}
                onCloseFunc={cleanup}
            >
                <div className={classes.locationContainer}>
                    <SiteAdminLocationField
                        setSearchLocation={setSearchLocation}
                        searchLocation={searchLocation}
                        siteLatitude={siteLatitude}
                        siteLongitude={siteLongitude}
                        defaultSearchText={defaultSearchText}
                    />
                    <MapContainer
                        className={classes.map}
                        center={defaultMapPosition}
                        zoom={MIN_MAP_ZOOM}
                        minZoom={MIN_MAP_ZOOM}
                        maxZoom={DEFAULT_MAX_MAP_ZOOM}
                        zoomSnap={0.1}
                        attributionControl={false}
                    >
                        <TileLayer noWrap url={tilesUrl} />
                        <LocationMarker
                            mapPosition={mapPosition}
                            setMapPosition={setMapPosition}
                            setMapLocation={setMapLocation}
                            mapSearchPosition={mapSearchPosition}
                            setSearchLocation={setSearchLocation}
                        />
                    </MapContainer>
                </div>
            </NgpTwoButtonDialog>
        </>
    );
}
